import { Container, Graphics } from 'pixi.js';
import { OFFICE_DOORS } from '@duck/contracts';
import { place, type RoomContext, type RoomDecorator } from './index';
import { registerScenery, synthCtx } from '../scenery';

// ── EDITABLE SCENERY REGISTRATIONS — expose this room's bespoke FURNITURE to the level editor so each
// piece can be moved/deleted/re-added like catalog furniture. Each lambda reproduces the EXACT build the
// matching place() call uses, anchored at the origin (synthCtx anchors at 0,0; none of these builders use
// ctx.cx/ctx.cy for geometry — they only read art + the colour helpers). The two sun-lounger sides and the
// planter are distinct kinds; backdrop (railing, string lights, washes) is NOT registered.
registerScenery('planter-box-wood', (art) => buildPlanter(synthCtx(art)), 'Planter box', '🪴');
registerScenery('sun-lounger-l', (art) => buildLoungeChair(synthCtx(art), 'left'), 'Sun lounger (left)', '🛋️');
registerScenery('sun-lounger-r', (art) => buildLoungeChair(synthCtx(art), 'right'), 'Sun lounger (right)', '🛋️');
registerScenery('parasol-striped', (art) => buildParasol(synthCtx(art)), 'Striped parasol', '⛱️');
registerScenery('outdoor-sofa-patio', (art) => buildOutdoorSofa(synthCtx(art)), 'Outdoor sofa', '🛋️');

/**
 * TERRACE — the office's open-air ROOFTOP GARDEN (rect 40,40 · 400×240; interior 40..440 × 40..280; centre 240,160).
 *
 * Redesigned for ORDER + STYLE — a balanced, resort-like rooftop. You step in from the kitchen through the
 * EAST door (x=440, y90..230) onto a sunny deck whose two OUTER edges — NORTH (top, y≈54) and WEST (left,
 * x≈54) — are a glass-panel RAILING you lean over for the skyline. The greenery is ARRANGED, not scattered:
 * a single tidy PLANTER ROW marches along the inside foot of those two outer railings (evenly-spaced ferns,
 * away from the door lane), with STRING LIGHTS swagging the north run for the warm bistro glow. NO loose pots
 * dot the floor; ALL plants live in the railing row.
 *
 * TWO balanced, fully-sittable seating sets — each on its OWN neat, axis-aligned outdoor rug:
 *
 *   • LEFT (south-west) — a sun-lounger PAIR flanking a low table under a striped PARASOL, on one rug.
 *     Both loungers are sittable rest spots (`terrace-lounger-l` / `terrace-lounger-r`).
 *   • RIGHT (south-east, BELOW the east door lane) — an outdoor SOFA facing the deck with TWO matching
 *     armchairs across a low table, on one rug. The sofa + both chairs are sittable
 *     (`terrace-sofa` / `terrace-chair-l` / `terrace-chair-r`).
 *
 * The EAST door lane (y90..230) is kept clear as a walk-in apron from the kitchen: the left set sits forward
 * in the south-west, the right set sits BELOW doorBottom, so nothing blocks the path in. Both halves read
 * furnished and balanced — no empty right side, no scattered floor pots, no stray rug blobs.
 *
 * SEAT ANCHORS (registered centrally in packages/contracts world/seats.ts; coordinates match the furniture
 * laid out here — facing is in radians: 0=right, π=left, −π/2=up, +π/2=down). All five land on a real,
 * visually-sittable piece and stay clear of the east walk-in apron:
 *   terrace-lounger-l  chair  (104, 226)  facing +½π  (left sun-lounger, reclined, looking out over the deck)
 *   terrace-lounger-r  chair  (204, 226)  facing +½π  (right sun-lounger)
 *   terrace-sofa       sofa   (356, 248)  facing −½π  (outdoor sofa, low against the SE south wall)
 *   terrace-chair-l    chair  (242, 234)  facing +½π  (west armchair across the table)
 *   terrace-chair-r    chair  (330, 234)  facing +½π  (east armchair across the table)
 *
 * Everything is themed off `art` (re-skins per world — deck, railing, parasol, lights, cushions all tint to
 * the world) and built ONCE on map build: no animation, no per-frame allocation. The whole scene stays inside
 * the rect with a margin off the walls, and the east door keeps a clear apron in front of it.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { container, zone, art, f, lighten } = ctx;

  // Room bounds (a small margin in from the walls; the east door lane is derived from the contract).
  const left = zone.x + 16; // 56  (the WEST railing line)
  const right = zone.x + zone.width - 16; // 424
  const back = zone.y + 14; // 54  (the NORTH railing line / outer edge)
  const front = zone.y + zone.height - 16; // 264
  const door = OFFICE_DOORS.find((d) => d.zoneId === zone.id && d.side === 'E');
  const doorY = door?.pos ?? 160; // ≈160 (centre of the y90..230 opening)
  const doorHalf = (door?.width ?? 140) / 2; // 70
  const doorBottom = doorY + doorHalf; // 230 — bottom of the east opening / walk-in lane

  // ── OPEN-SKY WASH: the deck is roofless, so a soft daylight bloom sits over the whole band, brightest
  // toward the two outer railings (north + west) — it reads as "you stepped outside" not another interior.
  const sky = lighten(art.windowSky, 0.12);
  container.addChild(
    new Graphics()
      .rect(zone.x, zone.y, zone.width, zone.height)
      .fill({ color: sky, alpha: 0.1 })
      .rect(zone.x, zone.y, zone.width, 44)
      .fill({ color: sky, alpha: 0.16 })
      .rect(zone.x, zone.y, 44, zone.height)
      .fill({ color: sky, alpha: 0.12 }),
  );

  // ── THE RAILINGS along the two outer edges. NORTH run spans the width (anchored top-left). The WEST run
  // reuses the same bespoke helper rotated −90° and anchored at the front-left corner, so it reads UP the
  // left edge with its glass depth facing INWARD (+x) — staying inside the west wall.
  place(ctx, buildRailing(right - left, ctx), left, back);
  const westRail = buildRailing(front - back, ctx);
  westRail.rotation = -Math.PI / 2; // local +x → up the edge, local +y (depth) → inward off the wall
  place(ctx, westRail, left, front);

  // ── STRING LIGHTS swagging along the north railing — the rooftop's signature warm bistro glow.
  container.addChild(buildStringLights(left, right, back - 2, art, lighten));

  // ── THE PLANTER ROW: greenery is ARRANGED, not scattered. One tidy, evenly-spaced run of planters marches
  // along the inside foot of the two outer railings — a long row under the NORTH rail and a short run down the
  // WEST rail — so every plant lives ON the railing line, never loose on the floor. The north run stops well
  // short of the east door lane so the walk-in stays read-clear; ferns sit on each box, foliage ≥16px off the
  // top wall. This is the ONLY greenery in the room (no floor pots, no corner bushes). ────────────────────
  const planterStep = 78;
  const northEnd = right - 92; // last north box stays clear of the east door lane / corner
  for (let px = left + 40; px <= northEnd; px += planterStep) {
    place(ctx, buildPlanter(ctx), px, back + 30, 'planter-box-wood');
    place(ctx, f.plant(art, 'fern'), px, back + 30); // foliage top stays ≥16px off the north wall
  }
  for (let py = back + 96; py <= front - 96; py += planterStep) {
    place(ctx, buildPlanter(ctx), left + 24, py, 'planter-box-wood'); // west run, hugging the west railing
    place(ctx, f.plant(art, 'leafy'), left + 24, py - 2);
  }

  // ── LEFT SEATING SET (south-west) — the HERO sun-lounger PAIR on ONE neat, axis-aligned rug: a low table
  // under a striped parasol flanked by two reclined loungers, well clear of the east door lane. The loungers
  // recline so a seated duck reads as looking OUT over the deck (facing down/+½π). ─────────────────────────
  const lx = left + 92; // ≈ 148 — set centre, in the south-west
  const ly = front - 46; // ≈ 218 — forward of the deck, rug bottom clears the south wall
  place(ctx, f.rug(art, 164, 80), lx, ly);
  place(ctx, f.coffeeTable(art), lx, ly - 2);
  // Two little drinks on the table (room-specific inline detail).
  container.addChild(
    new Graphics()
      .circle(lx - 8, ly - 4, 3.5)
      .fill(lighten(art.glow, 0.25))
      .circle(lx + 8, ly - 4, 3.5)
      .fill(lighten(art.accent, 0.2))
      .rect(lx - 9, ly - 10, 2, 5)
      .fill({ color: 0xffffff, alpha: 0.4 }) // a paper straw
      .rect(lx + 7, ly - 10, 2, 5)
      .fill({ color: 0xffffff, alpha: 0.4 }),
  );
  // SEAT: terrace-lounger-l  (lx−56, ly+8)  facing +½π (reclined, looking down/out over the deck)
  place(ctx, buildLoungeChair(ctx, 'left'), lx - 56, ly + 8, 'sun-lounger-l');
  // SEAT: terrace-lounger-r  (lx+56, ly+8)  facing +½π
  place(ctx, buildLoungeChair(ctx, 'right'), lx + 56, ly + 8, 'sun-lounger-r');
  // The striped parasol shades the table from behind (added after, so it layers above the chairs).
  place(ctx, buildParasol(ctx), lx + 20, ly - 10, 'parasol-striped');

  // ── RIGHT SEATING SET (south-east) — the SOCIAL conversation set, balancing the left lounge so both halves
  // read furnished. An outdoor SOFA sits low along the south wall on the EAST side (its body kept BELOW the
  // door lane, y>doorBottom, so it never touches the walk-in apron in front of the kitchen door), facing UP
  // (−½π) into the group. A low table and TWO matching armchairs form the conversation core on ONE neat,
  // axis-aligned rug to the sofa's west — the rug + chairs + table all stay WEST of the door apron's x-range,
  // so the whole east walk-in stays clear. ───────────────────────────────────────────────────────────────
  const rcx = 286; // conversation-core centre x (table + chairs), held west of the door apron (chair-r x≤352)
  const rcy = 234; // core centre y, in the south band; rug bottom clears the south wall
  place(ctx, f.rug(art, 128, 58), rcx, rcy);
  // SEAT: terrace-sofa  (356, doorBottom+18)  facing −½π — low against the SE south wall, below the door lane.
  const rsx = right - 68; // ≈ 356 — sofa centre, east side
  const rsy = doorBottom + 18; // ≈ 248 — sofa body fully below the door lane (y>doorBottom)
  place(ctx, buildOutdoorSofa(ctx), rsx, rsy, 'outdoor-sofa-patio');
  // Low table at the core centre, with a couple of drinks (room-specific inline detail).
  place(ctx, f.coffeeTable(art), rcx, rcy + 4);
  container.addChild(
    new Graphics()
      .circle(rcx - 7, rcy + 2, 3.2)
      .fill(lighten(art.glow, 0.25))
      .circle(rcx + 7, rcy + 2, 3.2)
      .fill(lighten(art.accent, 0.2)),
  );
  // TWO armchairs flanking the table on the rug, facing in toward the group (down/+½π toward the view).
  // SEAT: terrace-chair-l  (rcx−44, rcy)  facing +½π
  place(ctx, f.armchair(art), rcx - 44, rcy);
  // SEAT: terrace-chair-r  (rcx+44, rcy)  facing +½π
  place(ctx, f.armchair(art), rcx + 44, rcy);
};

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// Room-specific inline builders (terrace only — NOT added to the shared furniture catalog).
// Each returns a Pixi Container drawn in LOCAL space with a soft contact shadow at its base, matching
// the catalog contract; the room positions them with the shared `place(ctx, piece, x, y)` helper.
// (The railing is the one piece with a TOP-LEFT origin — it's a left-anchored run; the west edge reuses
//  it rotated 90°, so one helper dresses both outer edges.)
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/** A run of glass-panel railing (origin top-left): posts + cap rail + faintly-tinted glass infill. */
function buildRailing(w: number, ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const metal = mix(0xb8bcc6, art.wall, 0.25);
  const post = darken(metal, 0.2);
  const g = new Graphics();
  // Cap rail across the top + a lower rail.
  g.roundRect(0, 0, w, 5, 2).fill(metal).roundRect(0, 22, w, 4, 2).fill(darken(metal, 0.1));
  // Glass panels between evenly-spaced posts (a cool sky-tinted sheen, mostly transparent).
  for (let x = 0; x < w; x += 46) {
    g.rect(x, 5, 3, 18).fill(post); // post
    g.roundRect(x + 5, 4, 38, 19, 2).fill({ color: lighten(art.windowSky, 0.2), alpha: 0.16 });
    g.rect(x + 7, 5, 8, 16).fill({ color: 0xffffff, alpha: 0.1 }); // glass glint
  }
  g.rect(w - 3, 5, 3, 18).fill(post); // closing post
  c.addChild(g);
  return c;
}

/** Bistro STRING LIGHTS swagging across the deck (a dipping cord with little warm bulbs). */
function buildStringLights(x0: number, x1: number, y: number, art: RoomContext['art'], lighten: RoomContext['lighten']): Container {
  const c = new Container();
  const span = x1 - x0;
  const segs = Math.max(4, Math.round(span / 150));
  const cord = new Graphics();
  const bulb = new Graphics();
  for (let s = 0; s < segs; s++) {
    const ax = x0 + (span * s) / segs;
    const bx = x0 + (span * (s + 1)) / segs;
    const sag = 14;
    cord.moveTo(ax, y).quadraticCurveTo((ax + bx) / 2, y + sag, bx, y);
    // A couple of bulbs hung along each swag's dip.
    for (const t of [0.3, 0.5, 0.7]) {
      const bxp = ax + (bx - ax) * t;
      const byp = y + sag * (1 - (2 * t - 1) ** 2) + 4;
      bulb.circle(bxp, byp, 3.4).fill({ color: lighten(art.glow, 0.25), alpha: 0.95 });
      bulb.circle(bxp, byp, 6).fill({ color: art.glow, alpha: 0.14 }); // soft halo
    }
  }
  cord.stroke({ color: 0x3a3f4a, width: 1.5, alpha: 0.7 });
  c.addChild(cord, bulb);
  return c;
}

/** A rectangular PLANTER box that sits at the railing's foot, greenery placed on top separately. */
function buildPlanter(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const wood = mix(0x9c6a3a, art.wall, 0.22);
  c.addChild(new Graphics().ellipse(0, 12, 26, 6).fill({ color: 0x000000, alpha: 0.18 }));
  c.addChild(
    new Graphics()
      .roundRect(-24, -8, 48, 20, 3)
      .fill(wood)
      .roundRect(-24, -8, 48, 4, 3)
      .fill({ color: 0xffffff, alpha: 0.12 })
      // soil + a couple of slat lines
      .roundRect(-21, -7, 42, 3, 1)
      .fill(darken(0x4a3322, 0.1))
      .rect(-24, 1, 48, 1)
      .fill({ color: 0x000000, alpha: 0.12 }),
  );
  // A little trailing spill of greenery over the front lip.
  const leaf = mix(0x3f9d52, art.accent, 0.12);
  c.addChild(
    new Graphics()
      .ellipse(-14, 8, 5, 8)
      .fill(darken(leaf, 0.1))
      .ellipse(0, 9, 6, 9)
      .fill(leaf)
      .ellipse(15, 8, 5, 8)
      .fill(lighten(leaf, 0.08)),
  );
  return c;
}

/** An outdoor LOUNGE chair (sun-lounger silhouette): a slung seat with a raised headrest. */
function buildLoungeChair(ctx: RoomContext, side: 'left' | 'right'): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const dir = side === 'left' ? -1 : 1; // headrest leans toward the table (inward)
  const frame = mix(0xc8cdd6, art.wall, 0.2);
  const sling = mix(0xe8d2a8, art.accent, 0.22);
  c.addChild(new Graphics().ellipse(0, 10, 22, 6).fill({ color: 0x000000, alpha: 0.2 }));
  const g = new Graphics();
  // frame rails + little feet
  g.roundRect(-20, 2, 40, 4, 2).fill(frame).rect(-18, 6, 3, 5).fill(darken(frame, 0.2)).rect(15, 6, 3, 5).fill(darken(frame, 0.2));
  // slung seat cushion
  g.roundRect(-20, -6, 40, 10, 4).fill(sling).roundRect(-20, -6, 40, 3, 4).fill({ color: 0xffffff, alpha: 0.15 });
  // raised headrest at the inward end
  g.roundRect(dir > 0 ? 12 : -20, -16, 8, 14, 3).fill(lighten(sling, 0.06));
  // a couple of cushion seams
  g.moveTo(-6, -6).lineTo(-6, 4).moveTo(6, -6).lineTo(6, 4).stroke({ color: darken(sling, 0.12), width: 1, alpha: 0.5 });
  c.addChild(g);
  return c;
}

/** An OUTDOOR SOFA — a low two-seat patio couch with weatherproof cushions (faces up/into the deck). */
function buildOutdoorSofa(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const frame = mix(0xb89a72, art.wall, 0.22); // sun-bleached rattan/teak frame
  const cushion = mix(0xdcc6a0, art.accent, 0.2);
  c.addChild(new Graphics().ellipse(0, 12, 38, 9).fill({ color: 0x000000, alpha: 0.2 }));
  const g = new Graphics();
  // low frame base + stubby feet
  g.roundRect(-36, 2, 72, 8, 3)
    .fill(frame)
    .rect(-32, 9, 5, 5)
    .fill(darken(frame, 0.25))
    .rect(27, 9, 5, 5)
    .fill(darken(frame, 0.25));
  // backrest band (low, at the far/upper edge — the sofa opens toward the viewer/+y)
  g.roundRect(-36, -16, 72, 12, 5).fill(darken(cushion, 0.12)).roundRect(-36, -16, 72, 4, 5).fill({ color: 0xffffff, alpha: 0.12 });
  // two seat cushions
  for (const sx of [-18, 18]) {
    g.roundRect(sx - 17, -4, 34, 12, 5)
      .fill(cushion)
      .roundRect(sx - 17, -4, 34, 4, 5)
      .fill({ color: 0xffffff, alpha: 0.14 });
  }
  // arms
  g.roundRect(-40, -10, 8, 20, 3).fill(darken(frame, 0.08)).roundRect(32, -10, 8, 20, 3).fill(darken(frame, 0.08));
  // a bright throw pillow for life
  g.roundRect(-6, -2, 14, 11, 4).fill({ color: lighten(art.accent, 0.18), alpha: 0.9 });
  c.addChild(g);
  return c;
}

/** A striped PARASOL (sun umbrella): a pole rising to an open canopy of warm/light wedges. */
function buildParasol(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const canopyA = mix(0xf0f0f4, art.accent, 0.12);
  const canopyB = lighten(art.accent, 0.12);
  c.addChild(new Graphics().ellipse(0, 22, 10, 4).fill({ color: 0x000000, alpha: 0.18 }));
  // pole
  c.addChild(new Graphics().rect(-1.5, -30, 3, 52).fill(darken(art.wall, 0.25)));
  // canopy — alternating wedges fanned from the top of the pole
  const g = new Graphics();
  const r = 34;
  const top = -36;
  const slices = 6;
  for (let i = 0; i < slices; i++) {
    const a0 = Math.PI + (Math.PI * i) / slices;
    const a1 = Math.PI + (Math.PI * (i + 1)) / slices;
    g.moveTo(0, top)
      .lineTo(Math.cos(a0) * r, top + 12 + Math.sin(a0) * 6 + 6)
      .lineTo(Math.cos(a1) * r, top + 12 + Math.sin(a1) * 6 + 6)
      .closePath()
      .fill(i % 2 ? canopyA : canopyB);
  }
  // a soft scalloped lower edge + a finial on top
  g.ellipse(0, top + 14, r, 7).fill({ color: 0xffffff, alpha: 0.06 });
  g.circle(0, top - 2, 2.5).fill(darken(art.wall, 0.2));
  c.addChild(g);
  return c;
}

export default decorate;
